import { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  UserPlus, 
  Calendar,
  Search,
  Copy,
  CheckCircle2,
  Pencil,
  User,
  Loader2,
  Save
} from 'lucide-react';
import { useCurrency } from '@/context/CurrencyContext';
import { format } from 'date-fns';
import { Calendar as CalendarUI } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReceptionistPage = () => {
  const { formatPrice } = useCurrency();
  const [activeTab, setActiveTab] = useState('register');
  const [tests, setTests] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [bookingSearchQuery, setBookingSearchQuery] = useState('');
  const [allBookings, setAllBookings] = useState([]);
  
  // Registration form
  const [registrationData, setRegistrationData] = useState({
    email: '', full_name: '', phone: '', gender: '', date_of_birth: '', address: ''
  });
  
  // Booking form
  const [bookingData, setBookingData] = useState({
    patient_id: '', test_ids: [], appointment_date: null, appointment_time: '',
    payment_status: 'pending', payment_gateway: 'bank_transfer', notes: ''
  });
  
  // Edit states
  const [editingPatient, setEditingPatient] = useState(null);
  const [editPatientData, setEditPatientData] = useState({});
  const [editingBooking, setEditingBooking] = useState(null);
  const [editBookingData, setEditBookingData] = useState({});
  
  const [registrationResult, setRegistrationResult] = useState(null);
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);

  const timeSlots = [
    '07:00 AM', '08:00 AM', '09:00 AM', '10:00 AM', '11:00 AM',
    '12:00 PM', '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM',
    '05:00 PM', '06:00 PM'
  ];

  useEffect(() => {
    fetchTests();
    fetchPatients();
    fetchBookings();
  }, []);

  const fetchTests = async () => {
    try {
      const response = await axios.get(`${API}/tests`);
      setTests(response.data);
    } catch (error) { console.error('Error fetching tests:', error); }
  };

  const fetchPatients = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setPatients(response.data.filter(u => u.role === 'patient'));
    } catch (error) { console.error('Error fetching patients:', error); }
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`);
      setAllBookings(response.data);
    } catch (error) { console.error('Error fetching bookings:', error); }
  };

  const handleRegister = async () => {
    if (!registrationData.phone || !registrationData.full_name) {
      toast.error('Phone number and full name are required');
      return;
    }
    setSubmitting(true);
    try {
      const email = registrationData.email || `${registrationData.phone}@patient.factual.com`;
      const response = await axios.post(`${API}/receptionist/register-patient`, { ...registrationData, email });
      setRegistrationResult(response.data);
      setIsResultModalOpen(true);
      setRegistrationData({ email: '', full_name: '', phone: '', gender: '', date_of_birth: '', address: '' });
      fetchPatients();
      toast.success('Patient registered successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register patient');
    } finally { setSubmitting(false); }
  };

  const handleCreateBooking = async () => {
    if (!bookingData.patient_id || bookingData.test_ids.length === 0) {
      toast.error('Please select a patient and at least one test');
      return;
    }
    if (!bookingData.appointment_date || !bookingData.appointment_time) {
      toast.error('Please select appointment date and time');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...bookingData, appointment_date: format(bookingData.appointment_date, 'yyyy-MM-dd'), booking_type: 'walk_in' };
      await axios.post(`${API}/receptionist/create-booking`, payload);
      toast.success('Booking created successfully');
      setBookingData({ patient_id: '', test_ids: [], appointment_date: null, appointment_time: '', payment_status: 'pending', payment_gateway: 'bank_transfer', notes: '' });
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally { setSubmitting(false); }
  };

  const handleUpdatePatient = async () => {
    if (!editingPatient) return;
    setSubmitting(true);
    try {
      await axios.put(`${API}/receptionist/update-patient/${editingPatient.id}`, editPatientData);
      toast.success('Patient updated successfully');
      setEditingPatient(null);
      fetchPatients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update patient');
    } finally { setSubmitting(false); }
  };

  const handleUpdateBooking = async () => {
    if (!editingBooking) return;
    setSubmitting(true);
    try {
      await axios.put(`${API}/receptionist/update-booking/${editingBooking.id}`, editBookingData);
      toast.success('Booking updated successfully');
      setEditingBooking(null);
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update booking');
    } finally { setSubmitting(false); }
  };

  const openEditPatient = (patient) => {
    setEditingPatient(patient);
    setEditPatientData({
      full_name: patient.full_name || '',
      phone: patient.phone || '',
      email: patient.email || '',
      gender: patient.gender || '',
      date_of_birth: patient.date_of_birth || '',
      address: patient.address || ''
    });
  };

  const openEditBooking = (booking) => {
    setEditingBooking(booking);
    setEditBookingData({
      appointment_date: booking.appointment_date || '',
      appointment_time: booking.appointment_time || '',
      notes: booking.notes || '',
      payment_gateway: booking.payment_gateway || 'bank_transfer'
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const calculateTotal = () => {
    return bookingData.test_ids.reduce((total, testId) => {
      const test = tests.find(t => t.id === testId);
      return total + (test?.price || 0);
    }, 0);
  };

  const filteredPatients = patients.filter(p =>
    p.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredBookings = allBookings.filter(b =>
    b.id?.toLowerCase().includes(bookingSearchQuery.toLowerCase()) ||
    b.patient_id?.toLowerCase().includes(bookingSearchQuery.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="receptionist-page">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Front Desk</h1>
          <p className="text-muted-foreground mt-1">Register patients, create bookings, and manage records</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 max-w-2xl">
            <TabsTrigger value="register" data-testid="register-tab">
              <UserPlus className="w-4 h-4 mr-2" />Register
            </TabsTrigger>
            <TabsTrigger value="booking" data-testid="booking-tab">
              <Calendar className="w-4 h-4 mr-2" />Book
            </TabsTrigger>
            <TabsTrigger value="patients" data-testid="patients-tab">
              <User className="w-4 h-4 mr-2" />Patients
            </TabsTrigger>
            <TabsTrigger value="bookings" data-testid="bookings-tab">
              <Search className="w-4 h-4 mr-2" />Bookings
            </TabsTrigger>
          </TabsList>

          {/* Register Patient Tab */}
          <TabsContent value="register">
            <div className="card-clinical max-w-2xl">
              <h2 className="text-lg font-semibold text-foreground mb-6">Register Walk-in Patient</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Full Name *</Label>
                  <Input value={registrationData.full_name} onChange={(e) => setRegistrationData({...registrationData, full_name: e.target.value})} placeholder="Enter patient's full name" className="mt-1" data-testid="patient-name-input" />
                </div>
                <div>
                  <Label>Phone Number *</Label>
                  <Input value={registrationData.phone} onChange={(e) => setRegistrationData({...registrationData, phone: e.target.value})} placeholder="08012345678" className="mt-1" data-testid="patient-phone-input" required />
                </div>
                <div>
                  <Label>Email (Optional)</Label>
                  <Input type="email" value={registrationData.email} onChange={(e) => setRegistrationData({...registrationData, email: e.target.value})} placeholder="patient@email.com" className="mt-1" data-testid="patient-email-input" />
                </div>
                <div>
                  <Label>Gender</Label>
                  <Select value={registrationData.gender} onValueChange={(v) => setRegistrationData({...registrationData, gender: v})}>
                    <SelectTrigger className="mt-1" data-testid="patient-gender-select"><SelectValue placeholder="Select gender" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date of Birth</Label>
                  <Input type="date" value={registrationData.date_of_birth} onChange={(e) => setRegistrationData({...registrationData, date_of_birth: e.target.value})} className="mt-1" data-testid="patient-dob-input" />
                </div>
                <div className="md:col-span-2">
                  <Label>Address</Label>
                  <Input value={registrationData.address} onChange={(e) => setRegistrationData({...registrationData, address: e.target.value})} placeholder="Enter patient's address" className="mt-1" data-testid="patient-address-input" />
                </div>
              </div>
              <div className="mt-6">
                <Button onClick={handleRegister} disabled={submitting} className="bg-sky-600 hover:bg-sky-700" data-testid="register-patient-btn">
                  {submitting ? 'Registering...' : 'Register Patient'}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Create Booking Tab */}
          <TabsContent value="booking">
            <div className="card-clinical max-w-2xl">
              <h2 className="text-lg font-semibold text-foreground mb-6">Create Booking for Patient</h2>
              <div className="space-y-4">
                <div>
                  <Label>Select Patient *</Label>
                  <Select value={bookingData.patient_id} onValueChange={(v) => setBookingData({...bookingData, patient_id: v})}>
                    <SelectTrigger className="mt-1" data-testid="select-patient"><SelectValue placeholder="Search and select patient" /></SelectTrigger>
                    <SelectContent>
                      {patients.map(patient => (
                        <SelectItem key={patient.id} value={patient.id}>{patient.full_name} ({patient.phone || patient.email})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Select Tests *</Label>
                  <div className="mt-2 max-h-48 overflow-y-auto border border-border rounded-lg p-2">
                    {tests.map(test => (
                      <label key={test.id} className="flex items-center gap-3 p-2 hover:bg-muted rounded cursor-pointer">
                        <input type="checkbox" checked={bookingData.test_ids.includes(test.id)} onChange={(e) => {
                          if (e.target.checked) setBookingData({...bookingData, test_ids: [...bookingData.test_ids, test.id]});
                          else setBookingData({...bookingData, test_ids: bookingData.test_ids.filter(id => id !== test.id)});
                        }} className="rounded" />
                        <span className="flex-1 text-sm text-foreground">{test.name}</span>
                        <span className="text-sm font-medium text-sky-600">{formatPrice(test.price, 'NGN')}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Appointment Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full mt-1 justify-start" data-testid="booking-date-picker">
                          <Calendar className="w-4 h-4 mr-2" />
                          {bookingData.appointment_date ? format(bookingData.appointment_date, 'PPP') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarUI mode="single" selected={bookingData.appointment_date} onSelect={(date) => setBookingData({...bookingData, appointment_date: date})} disabled={(date) => { const today = new Date(); today.setHours(0,0,0,0); return date < today; }} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label>Appointment Time *</Label>
                    <Select value={bookingData.appointment_time} onValueChange={(v) => setBookingData({...bookingData, appointment_time: v})}>
                      <SelectTrigger className="mt-1" data-testid="booking-time-select"><SelectValue placeholder="Select time" /></SelectTrigger>
                      <SelectContent>
                        {timeSlots.map(slot => (<SelectItem key={slot} value={slot}>{slot}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Payment Method</Label>
                  <Select value={bookingData.payment_gateway} onValueChange={(v) => setBookingData({...bookingData, payment_gateway: v})}>
                    <SelectTrigger className="mt-1" data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="paystack">Paystack</SelectItem>
                      <SelectItem value="cash">Cash (Mark as Paid)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {bookingData.test_ids.length > 0 && (
                  <div className="p-4 bg-sky-50 dark:bg-sky-900/20 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-foreground">Total Amount</span>
                      <span className="text-2xl font-bold text-sky-600">{formatPrice(calculateTotal(), 'NGN')}</span>
                    </div>
                  </div>
                )}
                <div className="pt-4">
                  <Button onClick={handleCreateBooking} disabled={submitting} className="bg-sky-600 hover:bg-sky-700" data-testid="create-booking-btn">
                    {submitting ? 'Creating...' : 'Create Booking'}
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Patients List Tab */}
          <TabsContent value="patients">
            <div className="card-clinical">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-foreground">Patient Records</h2>
              </div>
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search by name, phone, or email..." className="pl-10" data-testid="patient-search" />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3 text-muted-foreground font-medium">Name</th>
                      <th className="text-left py-2 px-3 text-muted-foreground font-medium">Phone</th>
                      <th className="text-left py-2 px-3 text-muted-foreground font-medium">Email</th>
                      <th className="text-left py-2 px-3 text-muted-foreground font-medium">Gender</th>
                      <th className="text-right py-2 px-3 text-muted-foreground font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPatients.slice(0, 20).map(p => (
                      <tr key={p.id} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="py-2 px-3 font-medium">{p.full_name}</td>
                        <td className="py-2 px-3 text-muted-foreground">{p.phone || '-'}</td>
                        <td className="py-2 px-3 text-muted-foreground text-xs">{p.email}</td>
                        <td className="py-2 px-3 text-muted-foreground capitalize">{p.gender || '-'}</td>
                        <td className="py-2 px-3 text-right">
                          <Button variant="ghost" size="sm" onClick={() => openEditPatient(p)} data-testid={`edit-patient-${p.id}`}>
                            <Pencil className="w-4 h-4 mr-1" />Edit
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredPatients.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">No patients found</div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Bookings List Tab */}
          <TabsContent value="bookings">
            <div className="card-clinical">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-foreground">Recent Bookings</h2>
              </div>
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={bookingSearchQuery} onChange={(e) => setBookingSearchQuery(e.target.value)} placeholder="Search by booking ID..." className="pl-10" data-testid="booking-search" />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3 text-muted-foreground font-medium">Booking ID</th>
                      <th className="text-left py-2 px-3 text-muted-foreground font-medium">Date</th>
                      <th className="text-left py-2 px-3 text-muted-foreground font-medium">Time</th>
                      <th className="text-left py-2 px-3 text-muted-foreground font-medium">Status</th>
                      <th className="text-left py-2 px-3 text-muted-foreground font-medium">Payment</th>
                      <th className="text-right py-2 px-3 text-muted-foreground font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBookings.slice(0, 20).map(b => (
                      <tr key={b.id} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="py-2 px-3 font-mono text-xs">#{b.id?.slice(0,8)}</td>
                        <td className="py-2 px-3">{b.appointment_date || '-'}</td>
                        <td className="py-2 px-3">{b.appointment_time || '-'}</td>
                        <td className="py-2 px-3"><Badge variant="outline" className="capitalize">{b.status}</Badge></td>
                        <td className="py-2 px-3"><Badge variant="outline" className="capitalize">{b.payment_status}</Badge></td>
                        <td className="py-2 px-3 text-right">
                          {!['completed', 'cancelled'].includes(b.status) && (
                            <Button variant="ghost" size="sm" onClick={() => openEditBooking(b)} data-testid={`edit-booking-${b.id}`}>
                              <Pencil className="w-4 h-4 mr-1" />Edit
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredBookings.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">No bookings found</div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Registration Result Modal */}
      <Dialog open={isResultModalOpen} onOpenChange={setIsResultModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />Patient Registered Successfully
            </DialogTitle>
            <DialogDescription>Share these login credentials with the patient</DialogDescription>
          </DialogHeader>
          {registrationResult && (
            <div className="py-4 space-y-4">
              <div className="p-4 bg-muted rounded-lg space-y-3">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium text-foreground">{registrationResult.email}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(registrationResult.email)}><Copy className="w-4 h-4" /></Button>
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-muted-foreground">Temporary Password</p>
                    <p className="font-mono font-bold text-foreground">{registrationResult.temp_password}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(registrationResult.temp_password)}><Copy className="w-4 h-4" /></Button>
                </div>
              </div>
              <p className="text-sm text-amber-600">Note: The patient should change their password after first login.</p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setIsResultModalOpen(false)} className="bg-sky-600 hover:bg-sky-700">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Patient Modal */}
      <Dialog open={!!editingPatient} onOpenChange={(open) => !open && setEditingPatient(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Patient Information</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Full Name</Label>
              <Input value={editPatientData.full_name || ''} onChange={(e) => setEditPatientData({...editPatientData, full_name: e.target.value})} className="mt-1" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={editPatientData.phone || ''} onChange={(e) => setEditPatientData({...editPatientData, phone: e.target.value})} className="mt-1" />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={editPatientData.email || ''} onChange={(e) => setEditPatientData({...editPatientData, email: e.target.value})} className="mt-1" />
            </div>
            <div>
              <Label>Gender</Label>
              <Select value={editPatientData.gender || ''} onValueChange={(v) => setEditPatientData({...editPatientData, gender: v})}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select gender" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Address</Label>
              <Input value={editPatientData.address || ''} onChange={(e) => setEditPatientData({...editPatientData, address: e.target.value})} className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPatient(null)}>Cancel</Button>
            <Button onClick={handleUpdatePatient} disabled={submitting} className="bg-sky-600 hover:bg-sky-700">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Booking Modal */}
      <Dialog open={!!editingBooking} onOpenChange={(open) => !open && setEditingBooking(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Booking #{editingBooking?.id?.slice(0,8)}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Appointment Date</Label>
              <Input type="date" value={editBookingData.appointment_date || ''} onChange={(e) => setEditBookingData({...editBookingData, appointment_date: e.target.value})} className="mt-1" />
            </div>
            <div>
              <Label>Appointment Time</Label>
              <Select value={editBookingData.appointment_time || ''} onValueChange={(v) => setEditBookingData({...editBookingData, appointment_time: v})}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select time" /></SelectTrigger>
                <SelectContent>
                  {timeSlots.map(slot => (<SelectItem key={slot} value={slot}>{slot}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select value={editBookingData.payment_gateway || ''} onValueChange={(v) => setEditBookingData({...editBookingData, payment_gateway: v})}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="paystack">Paystack</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={editBookingData.notes || ''} onChange={(e) => setEditBookingData({...editBookingData, notes: e.target.value})} className="mt-1" placeholder="Optional notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingBooking(null)}>Cancel</Button>
            <Button onClick={handleUpdateBooking} disabled={submitting} className="bg-sky-600 hover:bg-sky-700">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default ReceptionistPage;
